import { FC, ReactNode, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

interface Props {
  open: boolean;
  title: string;
  /** Explains which word has to be typed - it should name `confirmationText`. */
  instruction: string;
  /** The word the user has to type. Compared case-insensitively. */
  confirmationText: string;
  confirmLabel: string;
  cancelLabel: string;
  confirmColor?: 'primary' | 'error';
  /** What is about to happen - shown above the confirmation input. */
  children: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog for irreversible actions. The confirm button stays disabled until the user
 * types `confirmationText`, so the action cannot be triggered by a misplaced click.
 */
export const ConfirmByTypingModal: FC<Props> = ({
  open,
  title,
  instruction,
  confirmationText,
  confirmLabel,
  cancelLabel,
  confirmColor = 'primary',
  children,
  onConfirm,
  onCancel,
}) => {
  const [confirmationInput, setConfirmationInput] = useState('');

  const isConfirmDisabled = confirmationInput.trim().toLowerCase() !== confirmationText.toLowerCase();

  const handleClose = () => {
    setConfirmationInput('');
    onCancel();
  };

  const handleConfirm = () => {
    if (isConfirmDisabled) return;
    setConfirmationInput('');
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          {children}

          <Typography variant="body2" sx={{ mb: 1 }}>
            {instruction}
          </Typography>

          <TextField
            fullWidth
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
            placeholder={confirmationText}
            autoFocus
            sx={{ mt: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          {cancelLabel}
        </Button>
        <Button onClick={handleConfirm} variant="contained" color={confirmColor} disabled={isConfirmDisabled}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
